import {
  AdjustmentType,
  OverrideSource,
  StockHistoricalPriceItem,
  StockOverrideRecord,
} from "../../../../types/historicalOverrides";
import { formatNumberFixed } from "../../../../utils/numberUtils";
import { PriceRange } from "./useStockPrices";

export interface OverrideFormState {
  source: OverrideSource;
  startAtLocal: string;
  endAtLocal: string;
  adjustmentType: AdjustmentType;
  adjustmentFactor: string;
  adjustmentAbsolute: string;
  reason: string;
  isActive: boolean;
}

export const EMPTY_FORM_STATE: OverrideFormState = {
  source: "IBKR",
  startAtLocal: "",
  endAtLocal: "",
  adjustmentType: "MANUAL",
  adjustmentFactor: "",
  adjustmentAbsolute: "",
  reason: "",
  isActive: true,
};

export const UTC_INPUT_PLACEHOLDER = "YYYY-MM-DD HH:MM";
const UTC_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/;

/**
 * Converts an ISO string (e.g. "2026-04-14T13:05:00.000Z") into the
 * 24-hour text representation shown in the UTC input fields:
 * "2026-04-14 13:05".
 */
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
    ` ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
  );
}

/**
 * Inverse of isoToDatetimeLocal. Accepts either a space or "T" between the
 * date and time to be lenient with paste input, and treats the value as a
 * UTC wall clock. Returns "" when the input does not look like a valid
 * "YYYY-MM-DD HH:MM" UTC timestamp.
 */
export function datetimeLocalToIso(local: string): string {
  if (!local) return "";
  const match = UTC_INPUT_PATTERN.exec(local.trim());
  if (!match) return "";
  const [, y, mo, d, h, mi] = match;
  const iso = `${y}-${mo}-${d}T${h}:${mi}:00Z`;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString();
}

/**
 * True when the given text is a parseable UTC input string. Used by the
 * form inputs and validation to reject free-text typos before submit.
 */
export function isValidDatetimeLocal(local: string): boolean {
  return datetimeLocalToIso(local) !== "";
}

export function formatUtc(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
    ` ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
  );
}

/**
 * Converts an ISO string to the HTML datetime-local input value format
 * (YYYY-MM-DDTHH:MM) using UTC components. Returns "" for invalid input.
 */
export function isoToPickerValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
  );
}

/**
 * Converts an HTML datetime-local value (YYYY-MM-DDTHH:MM) to an ISO string,
 * treating the value as UTC. Returns "" for invalid input.
 */
export function pickerValueToIso(pickerVal: string): string {
  if (!pickerVal) return "";
  const iso = pickerVal.includes("T") ? `${pickerVal}:00Z` : `${pickerVal}T00:00:00Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

// ---- V2 types ----

export interface EnrichedPriceRow extends StockHistoricalPriceItem {
  isFailed: boolean;
  isOverridden: boolean;
  overrideId: number | null;
  overrideValue: string | null;
  isSelected: boolean;
  pendingValue: string | null;
}

/**
 * Returns the "effective" close for a row: pending override > saved override > raw close.
 * Used by the chart to show realtime preview of edits.
 */
export function effectiveClose(row: EnrichedPriceRow): number {
  if (row.pendingValue) {
    const v = Number(row.pendingValue);
    if (!Number.isNaN(v)) return v;
  }
  if (row.overrideValue) {
    const v = Number(row.overrideValue);
    if (!Number.isNaN(v)) return v;
  }
  return Number(row.close);
}

export function computeInitialRange(
  failureSlots?: string[],
  lookbackHours?: number
): PriceRange {
  if (failureSlots && failureSlots.length > 0) {
    const times = failureSlots.map((s) => new Date(s).getTime()).filter((t) => !Number.isNaN(t));
    if (times.length > 0) {
      const minT = Math.min(...times);
      const maxT = Math.max(...times);
      const padMs = 6 * 60 * 60 * 1000;
      return {
        from: new Date(minT - padMs).toISOString(),
        to: new Date(maxT + padMs).toISOString(),
      };
    }
  }
  const to = new Date();
  const fromMs = lookbackHours
    ? lookbackHours * 60 * 60 * 1000
    : 24 * 60 * 60 * 1000;
  return {
    from: new Date(to.getTime() - fromMs).toISOString(),
    to: to.toISOString(),
  };
}

/**
 * Find the close price of the last non-failed slot before the first selected
 * failed slot. Returns null if no good predecessor exists.
 */
export function findCarryForwardPrices(
  rows: EnrichedPriceRow[],
  selectedFailedSlots: string[],
  failureSlots: Set<string>
): Record<string, string> | null {
  const targets = new Set(selectedFailedSlots);
  const result: Record<string, string> = {};
  let lastGood: string | null = null;
  for (const row of rows) {
    if (targets.has(row.datetimeUtc)) {
      if (lastGood) result[row.datetimeUtc] = lastGood;
    }
    if (!failureSlots.has(row.datetimeUtc)) {
      const value = effectiveClose(row);
      if (!Number.isNaN(value)) {
        lastGood = formatNumberFixed(value, 2);
      }
    }
  }
  return Object.keys(result).length > 0 ? result : null;
}

export function isSlotInOverride(
  slotIso: string,
  override: StockOverrideRecord
): boolean {
  const t = new Date(slotIso).getTime();
  const start = new Date(override.startAtUtc).getTime();
  const end = override.endAtUtc ? new Date(override.endAtUtc).getTime() : Infinity;
  return t >= start && t <= end;
}

function overrideSpanMs(override: StockOverrideRecord): number {
  const start = new Date(override.startAtUtc).getTime();
  const end = override.endAtUtc ? new Date(override.endAtUtc).getTime() : Infinity;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return Infinity;
  return Math.max(0, end - start);
}

function overrideCreatedAtMs(override: StockOverrideRecord): number {
  const created = new Date(override.createdAtUtc).getTime();
  return Number.isNaN(created) ? 0 : created;
}

export function resolveOverrideForSlot(
  slotIso: string,
  overrides: StockOverrideRecord[]
): StockOverrideRecord | null {
  const matches = overrides.filter((override) => isSlotInOverride(slotIso, override));
  if (matches.length === 0) return null;

  return matches.reduce((best, candidate) => {
    const bestSpan = overrideSpanMs(best);
    const candidateSpan = overrideSpanMs(candidate);
    if (candidateSpan !== bestSpan) {
      return candidateSpan < bestSpan ? candidate : best;
    }

    const bestCreatedAt = overrideCreatedAtMs(best);
    const candidateCreatedAt = overrideCreatedAtMs(candidate);
    if (candidateCreatedAt !== bestCreatedAt) {
      return candidateCreatedAt > bestCreatedAt ? candidate : best;
    }

    return candidate.id > best.id ? candidate : best;
  });
}
